import React, { useEffect, useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import "../../tailwind.css";
import { mjDebugLog, mjDebugError } from "../../utils/logger";
import lottie from "lottie-web";
import scratchOffAnimationData from "../../static/json/pay/scratch-off-prize.json";
import fireworksAnimationData from "../../static/json/pay/fireworks.json";
import { logEvent as trackEvent } from "../../tracking/tracking";
import { getLoading } from "../../utils/loading";

const handImage = "/static/image/pay/scratch-off-prize/hand.png";
const bgImage = "/static/image/pay/scratch-off-prize-bg.png";
const scratchBgImage = "/static/image/pay/scratch-bg.jpg";

const logEventImmediately = ({ name }) => {
    trackEvent(name, {}, "", true);
};

const ScratchOffPrize = ({ onComplete, discount = "61", planType = "workout", userName: initialUserName = "" }) => {
    const [isScratching, setIsScratching] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);
    const [showGuideAnimation, setShowGuideAnimation] = useState(true);
    const [userName, setUserName] = useState("User");
    const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
    const guideAnimationRef = useRef(null);
    const fireworksAnimationRef = useRef(null);
    const guideAnimationInstance = useRef(null);
    const fireworksAnimationInstance = useRef(null);
    const scratchAreaRef = useRef(null);
    const canvasRef = useRef(null);
    const contextRef = useRef(null);
    const hasLoggedSlide = useRef(false);
    const autoRevealTimer = useRef(null);
    const hasRevealed = useRef(false);

    useEffect(() => {
        const loading = getLoading();
        loading.show("rgba(0, 0, 0, 0.5)");

        const img = new Image();
        img.onload = () => {
            setIsBackgroundLoaded(true);
            setTimeout(() => {
                loading.hide();
            }, 500);
            mjDebugLog("background image loaded", {});
        };
        img.onerror = () => {
            mjDebugError("background image load failed", {});
            setIsBackgroundLoaded(true);
            setTimeout(() => {
                loading.hide();
            }, 500);
        };
        img.src = bgImage;

        logEventImmediately({ name: "scratchprize_show" });

        if (initialUserName) {
            setUserName(initialUserName);
        } else if (window.contentMap?.username?.name) {
            setUserName(window.contentMap.username.name);
        }

        return () => {
            loading.hide();
        };
    }, [initialUserName]);

    useEffect(() => {
        if (!isBackgroundLoaded) return;

        if (guideAnimationRef.current && showGuideAnimation) {
            const processedAnimationData = JSON.parse(
                JSON.stringify(scratchOffAnimationData).replace(/images\//g, "").replace(/hand\.png/g, handImage)
            );

            guideAnimationInstance.current = lottie.loadAnimation({
                container: guideAnimationRef.current,
                renderer: "svg",
                loop: true,
                autoplay: true,
                animationData: processedAnimationData,
                rendererSettings: {
                    preserveAspectRatio: "xMidYMin meet",
                    progressiveLoad: true,
                },
            });
        }

        return () => {
            if (guideAnimationInstance.current) {
                guideAnimationInstance.current.destroy();
                guideAnimationInstance.current = null;
            }
        };
    }, [showGuideAnimation, isBackgroundLoaded]);

    useEffect(() => {
        if (!isBackgroundLoaded || !canvasRef.current || !scratchAreaRef.current || isRevealed) return;

        const canvas = canvasRef.current;
        const scratchArea = scratchAreaRef.current;
        const rect = scratchArea.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        contextRef.current = ctx;
        ctx.fillStyle = "#F1F3F6";
        ctx.fillRect(0, 0, rect.width, rect.height);
        // Load and draw scratch background image
        const img = new Image();
        img.onload = () => {
            ctx.globalAlpha = 0.02; // 2% opacity, very transparent
            ctx.drawImage(img, 0, 0, rect.width, rect.height);
            ctx.globalAlpha = 1.0; // Reset alpha
        };
        img.src = scratchBgImage;

        const scratchRadius = 30;

        const scratch = (x, y) => {
            ctx.globalCompositeOperation = "destination-out";
            ctx.beginPath();
            ctx.arc(x, y, scratchRadius, 0, 2 * Math.PI);
            ctx.fill();
        };

        const clearAllScratch = () => {
            ctx.clearRect(0, 0, rect.width, rect.height);
        };

        const handleStart = (e) => {
            e.preventDefault();
            if (!isScratching && showGuideAnimation) {
                setShowGuideAnimation(false);
            }

            if (!hasLoggedSlide.current) {
                hasLoggedSlide.current = true;
                logEventImmediately({ name: "scratchslide_click" });
                mjDebugLog("user slide scratch", {});

                // Set auto-reveal timer as insurance
                if (autoRevealTimer.current) {
                    clearTimeout(autoRevealTimer.current);
                }
                autoRevealTimer.current = setTimeout(() => {
                    mjDebugLog("auto reveal prize after 5s", {});
                    clearAllScratch();
                    revealPrize();
                }, 5000);
            }

            setIsScratching(true);

            const rect = canvas.getBoundingClientRect();
            const touch = e.touches ? e.touches[0] : e;
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            scratch(x, y);
        };

        const handleMove = (e) => {
            if (!isScratching) return;
            e.preventDefault();

            const rect = canvas.getBoundingClientRect();
            const touch = e.touches ? e.touches[0] : e;
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            scratch(x, y);
        };

        const handleEnd = () => {
            if (isScratching) {
                setIsScratching(false);
                clearAllScratch();
                revealPrize();
            }
        };

        canvas.addEventListener("touchstart", handleStart, { passive: false });
        canvas.addEventListener("touchmove", handleMove, { passive: false });
        canvas.addEventListener("touchend", handleEnd);
        canvas.addEventListener("touchcancel", handleEnd);
        canvas.addEventListener("mousedown", handleStart);
        canvas.addEventListener("mousemove", handleMove);
        canvas.addEventListener("mouseup", handleEnd);

        document.addEventListener("touchend", handleEnd);
        document.addEventListener("touchcancel", handleEnd);
        document.addEventListener("mouseup", handleEnd);

        return () => {
            canvas.removeEventListener("touchstart", handleStart);
            canvas.removeEventListener("touchmove", handleMove);
            canvas.removeEventListener("touchend", handleEnd);
            canvas.removeEventListener("touchcancel", handleEnd);
            canvas.removeEventListener("mousedown", handleStart);
            canvas.removeEventListener("mousemove", handleMove);
            canvas.removeEventListener("mouseup", handleEnd);

            document.removeEventListener("touchend", handleEnd);
            document.removeEventListener("touchcancel", handleEnd);
            document.removeEventListener("mouseup", handleEnd);

            // Clear auto-reveal timer on cleanup
            if (autoRevealTimer.current) {
                clearTimeout(autoRevealTimer.current);
                autoRevealTimer.current = null;
            }
        };
    }, [isScratching, showGuideAnimation, isRevealed, isBackgroundLoaded]);

    const revealPrize = () => {
        if (hasRevealed.current) return;
        hasRevealed.current = true;
        setIsRevealed(true);

        // Clear auto-reveal timer
        if (autoRevealTimer.current) {
            clearTimeout(autoRevealTimer.current);
            autoRevealTimer.current = null;
        }

        mjDebugLog("scratch prize revealed", {});

        setTimeout(() => {
            if (fireworksAnimationRef.current) {
                fireworksAnimationInstance.current = lottie.loadAnimation({
                    container: fireworksAnimationRef.current,
                    renderer: "svg",
                    loop: false,
                    autoplay: true,
                    animationData: fireworksAnimationData,
                    rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice",
                        progressiveLoad: true,
                    },
                });

                fireworksAnimationInstance.current.addEventListener("complete", () => {
                    if (fireworksAnimationInstance.current) {
                        fireworksAnimationInstance.current.destroy();
                        fireworksAnimationInstance.current = null;
                    }
                    logEventImmediately({ name: "scratchauto_hide" });
                    mjDebugLog("scratch prize auto hide", {});
                    if (onComplete) {
                        onComplete();
                    }
                });
            } else if (onComplete) {
                onComplete();
            }
        }, 100);
    };

    if (!isBackgroundLoaded) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-[2rem]">
            <div
                className="relative w-full max-w-[33.5rem] overflow-hidden"
                style={{
                    aspectRatio: '335/530',
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {isRevealed && (
                    <div ref={fireworksAnimationRef} className="absolute inset-0 pointer-events-none z-10" />
                )}

                <div className="h-full flex flex-col pt-[8rem] pb-[8rem]">
                    <div className="text-center mb-[2rem] px-[2rem]">
                        <h2 className="text-[2.4rem] font-bold leading-[3.2rem] text-[#090909] mb-[1.2rem]">
                            {userName}, scratch to reveal your special discount!
                        </h2>
                        <p className="text-[1.6rem] leading-[2.4rem] text-[#666666]">
                            We want you to start your journey with a nice surprise
                        </p>
                    </div>

                    <div className="w-full h-[1px] border-t border-dashed border-[#DABCC2] mb-[2.4rem] mx-[2rem]"></div>

                    <div className="relative flex flex-col justify-center px-[2rem]">
                        {showGuideAnimation && (
                            <div className="absolute inset-0 pointer-events-none z-[20]">
                                <div
                                    ref={guideAnimationRef}
                                    className="w-full"
                                    style={{ aspectRatio: '1125/840' }}
                                />
                            </div>
                        )}

                        <div className="relative">
                            <div
                                ref={scratchAreaRef}
                                className="relative w-full aspect-[295/150] bg-white rounded-[20px] overflow-hidden"
                            >
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <div className="flex items-center gap-[0.4rem]">
                                        <div className="text-[6.4rem] font-extrabold text-[#F91D5F] leading-[6.4rem] italic">
                                            {discount}
                                        </div>
                                        <div className="flex flex-col items-center justify-center h-full">
                                            <div className="text-[2.4rem] font-extrabold text-[#F91D5F] leading-[2rem] italic">
                                                %
                                            </div>
                                            <div className="text-[2rem] font-extrabold text-[#F91D5F] leading-[1.6rem] tracking-wider italic">
                                                OFF
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-[1.5rem] text-[#666666] mt-[0.8rem] font-semibold">
                                        on your Personalized
                                    </div>
                                    <div className="text-[1.5rem] text-[#666666] font-semibold">
                                        {planType} Plan
                                    </div>
                                </div>

                                <canvas
                                    ref={canvasRef}
                                    className={`absolute inset-0 w-full h-full touch-none cursor-pointer z-[1] ${isRevealed ? "hidden" : ""
                                        }`}
                                />
                            </div>
                        </div>
                    </div>

                    {!isRevealed && (
                        <div className="text-center mt-[1rem] text-[1.4rem] text-[#A3A3A3] px-[2rem]">
                            Scratch it off
                        </div>
                    )}

                    {isRevealed && (
                        <div className="text-center mt-[1rem] text-[1.4rem] text-[#A3A3A3] px-[2rem]">
                            Applied automatically at checkout
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export function showScratchOffPrize({ onComplete, discount, planType, userName }) {
    const container = document.createElement("div");
    container.id = "scratch-off-prize-root";
    document.body.appendChild(container);

    const root = createRoot(container);

    const handleComplete = () => {
        root.unmount();
        document.body.removeChild(container);
        if (onComplete) {
            onComplete();
        }
    };

    root.render(
        <ScratchOffPrize onComplete={handleComplete} discount={discount} planType={planType} userName={userName} />
    );
}

export default ScratchOffPrize;
